import React from 'react';
import { sanitize } from 'helpers/utils/htmlSanitize';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';

import { GenericSectionContent, MediaCard, Cta } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import styles from './index.module.css';

export default function AtlasRendering({ content }: { content: GenericSectionContent }) {
  const { cards, additional_css, additional_css_class_names } = content;

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'AtlasRendering');

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`relative w-full pt-24 pb-24 lg:pt-40 lg:pb-40 2xl:pt-60 2xl:pb-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        {cards?.map((slide: MediaCard, index: number) => {
          const hasContent = Boolean(slide?.title || slide?.description || slide?.ctas?.length);
          const isCenter = slide?.content_alignment === 'Center';
          return (
            //The image is a background layer, so the section's height comes from its copy: at least the
            //legacy banner heights (all an image-only Atlas ever is), growing with the copy's own padding.
            <div
              key={index}
              className="relative flex min-h-[23rem] w-full items-center justify-center overflow-hidden lg:min-h-[32rem] 3xl:min-h-[40rem]"
            >
              {slide?.desktop_image?.url && (
                <Image
                  loading='lazy'
                  src={slide?.desktop_image?.url}
                  alt={slide?.desktop_image?.description ?? ''}
                  fill
                  style={{ objectFit: 'cover' }}
                  className="brightness-50"
                />
              )}

              {/* Skipped when empty: an image-only Atlas (the renovation-guide banner) must not emit an empty <h1>. */}
              {hasContent && (
                <div
                  className={`relative w-full px-24 py-60 lg:w-[47rem] lg:px-0 lg:py-80 2xl:py-120 ${
                    isCenter ? 'text-center' : 'text-left'
                  }`}
                >
                  {slide?.title && (
                    <Typography
                      as="h1"
                      className="text-32 font-semibold leading-10 text-white drop-shadow-sm 2xl:text-42"
                    >
                      {slide.title}
                    </Typography>
                  )}
                  {slide?.description && (
                    <div
                      className={`${slide?.title ? 'mt-24 lg:mt-32' : ''} whitespace-pre-line text-20 font-normal leading-7 text-white lg:leading-10 xl:text-18 2xl:text-20`}
                      dangerouslySetInnerHTML={{ __html: sanitize(slide.description) }}
                    />
                  )}
                  {slide?.ctas && slide?.ctas.length > 0 && (
                    <div
                      className={`${slide?.title || slide?.description ? 'mt-32 lg:mt-48' : ''} flex flex-wrap gap-16 ${
                        isCenter ? 'justify-center' : 'justify-start'
                      }`}
                    >
                      {slide.ctas.map((cta: Cta, ctaIndex: number) => (
                        <Link link={cta?.url} target={cta?.target} key={ctaIndex}>
                          <Button className="border-1 rounded-none border border-[#2B2C39] bg-white px-25 py-13 shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4]">
                            <Typography as="span" className="text-16 text-[#090909] xl:text-14 2xl:text-16">
                              {cta?.label}
                            </Typography>
                          </Button>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
